import {
  ConnectionListResult,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseUpstreamFlowsFilteredByTableQuery,
  DatabaseUpstreamFlowsFilteredByTableQueryVariables
} from '../../../gen-src/queries/DatabaseUpstreamFlowsFilteredByTableQuery';
import {
  DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQuery,
  DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQueryVariables
} from '../../../gen-src/queries/DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQuery';
import {
  DatabaseUpstreamFlowsQuery,
  DatabaseUpstreamFlowsQueryVariables,
} from '../../../gen-src/queries/DatabaseUpstreamFlowsQuery';
import {
  DatabaseUpstreamFlowsWithoutPersonalSpaceQuery,
  DatabaseUpstreamFlowsWithoutPersonalSpaceQueryVariables,
} from '../../../gen-src/queries/DatabaseUpstreamFlowsWithoutPersonalSpaceQuery';
import { FlowsDisplayGridRowDataFragment } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragment';
import { FlowsDisplayGridRowDataFragmentWithoutPersonalSpace } from '../../../gen-src/queries/FlowsDisplayGridRowDataFragmentWithoutPersonalSpace';
import { FlowSortOrder } from '../../../gen-src/queries/globalTypes';
import { DatabaseUpstreamFlowsFilteredByTableQueryGql } from '../../queries/DatabaseUpstreamFlowsFilteredByTableQueryGql';
import { DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQueryGql';
import { DatabaseUpstreamFlowsQueryGql } from '../../queries/DatabaseUpstreamFlowsQueryGql';
import { DatabaseUpstreamFlowsWithoutPersonalSpaceQueryGql } from '../../queries/DatabaseUpstreamFlowsWithoutPersonalSpaceQueryGql';
import {
  FlowsDisplayGrid,
  FlowsDisplayGridRowData,
  getRowDataForFlow,
  getRowDataForFlowWithoutPersonalSpace,
} from '../sharedDisplayGrid/FlowsDisplayGrid';
import { FlowsValidSortFields } from '../sorting/sortProviders/FlowsGridSortProvider';

type QueryVariables = DatabaseUpstreamFlowsQueryVariables;
type QueryResults = DatabaseUpstreamFlowsQuery;
type QueryResultListNode = FlowsDisplayGridRowDataFragment;
type GridRowData = FlowsDisplayGridRowData;

type QueryVariablesWithoutPersonalSpace = DatabaseUpstreamFlowsWithoutPersonalSpaceQueryVariables;
type QueryResultsWithoutPersonalSpace = DatabaseUpstreamFlowsWithoutPersonalSpaceQuery;
type QueryResultListNodeWithoutPersonalSpace = FlowsDisplayGridRowDataFragmentWithoutPersonalSpace;

type QueryVariablesWithFilter = DatabaseUpstreamFlowsFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseUpstreamFlowsFilteredByTableQuery;

type QueryVariablesWithFilterWithoutPersonalSpace = DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQueryVariables;
type QueryResultsWithFilterWithoutPersonalSpace = DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] && resultData.databases[0].upstreamFlowsConnection;
}

function selectConnectionListResultWithoutPersonalSpace(resultData: QueryResultsWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.databases && resultData.databases[0] && resultData.databases[0].upstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQuery(resultData: QueryResultsWithFilter): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] && resultData.tables[0].upstreamFlowsConnection;
}

function selectConnectionListResultForFilteredQueryWithoutPersonalSpace(resultData: QueryResultsWithFilterWithoutPersonalSpace):
  ConnectionListResult<QueryResultListNodeWithoutPersonalSpace> | null {
  return resultData && resultData.tables && resultData.tables[0] && resultData.tables[0].upstreamFlowsConnection;
}

export type DatabaseUpstreamFlowsQueryGridProps = GridSortProps<FlowsValidSortFields> & {
  readonly databaseId: string;
  readonly filteredTableId: string | null;
  readonly rowCount: number | null;
};

export function DatabaseUpstreamFlowsQueryGrid(props: DatabaseUpstreamFlowsQueryGridProps): JSX.Element {
  const { personalSpaceEnabled } = React.useContext(FeatureFlagsContext);
  const emptyGridMessage = Messages.EmptyUpstreamFlowsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyUpstreamFilteredFlowsLineageGridMessage();

  return personalSpaceEnabled ? (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
        queryGql={DatabaseUpstreamFlowsFilteredByTableQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQuery}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={DatabaseUpstreamFlowsQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForFlow}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  ) : (
    props.filteredTableId ? (
      <PaginatedQueryWrapper<QueryVariablesWithFilterWithoutPersonalSpace, QueryResultsWithFilterWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseUpstreamFlowsFilteredByTableWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ tableId: props.filteredTableId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultForFilteredQueryWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyFilteredGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    ) : (
      <PaginatedQueryWrapper<QueryVariablesWithoutPersonalSpace, QueryResultsWithoutPersonalSpace,
      QueryResultListNodeWithoutPersonalSpace, GridRowData>
        queryGql={DatabaseUpstreamFlowsWithoutPersonalSpaceQueryGql}
        partialQueryVariables={{ databaseId: props.databaseId, orderBy: props.orderBy as FlowSortOrder }}
        selectConnectionListResult={selectConnectionListResultWithoutPersonalSpace}
        mapResultNode={getRowDataForFlowWithoutPersonalSpace}
      >
        {({ rowStore }) => (
          <FlowsDisplayGrid
            rowStore={rowStore}
            emptyGridMessage={emptyGridMessage}
            rowCount={props.rowCount}
            orderBy={props.orderBy}
            updateSortOrder={props.updateSortOrder}
          />
        )}
      </PaginatedQueryWrapper>
    )
  );
}
